'use client';

import { DefaultPageProps } from '@/lib/types/shared/page.types';
import { Container, Input, Typography } from '@mui/material';
import React, { useState } from 'react';
import { dispatchLeasePackagesModalOpen } from '@/lib/helpers/modals/PackagesModalHelper';
import { ButtonNew, Image } from '@/components/shared';
import { Table } from '@/components/shared';
import { useTranslations } from 'next-intl';
import { convertMarkdownBolds } from '@/lib/utils';
import { MoveFadeInAnimation } from '@/lib/hooks/use-animations';
import { ButtonOne } from "@/components/shared/index"
import ErrorMessage from "@/components/shared/forms/ErrorMessage";
import { useFormik } from 'formik';

import { FormErrors, FormValues } from "@/lib/types/shared/page.types";
import emailTemplateConfigInfo from '@/lib/constants/mailTemplates/emailTemplateConfigInfo';
import { sendEmail } from '@/lib/api/EmailApi';
import { route } from '@/lib/navigationFunctions';
import { Cursor } from './icons';

const packages = [
    {
        id: 'standard',
        contentCount: 9,
    },
    {
        id: 'professional',
        contentCount: 9,
    },
    {
        id: 'premium',
        contentCount: 9,
    },
];

const ValidateForm = (values: FormValues) => {
    const errors: FormErrors = {};
    
    // Email Validation
    if (!values.email) {
        errors.email = 'required';
    } else if (!/^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,4}$/i.test(values.email)) {
        errors.email = 'invalid_email_address';
    } else {
        if (errors.email) delete errors.email;
    }

    return errors;
}

const PricingSectionFinancialLease = ({ id = '', params, disableComparisonTable = false, disableInfoText = false, disablePackagesSection = false, disableWarningText = false, buttonType = 'new', hideConfiguratorPackage = false }: { id?: string, disableComparisonTable?: boolean, disableInfoText?: boolean, disablePackagesSection?: boolean, disableWarningText?: boolean, buttonType?: 'new' | 'old', hideConfiguratorPackage?: boolean } & DefaultPageProps) => {
    const t = useTranslations('pages.financial_website_lease');
    const t_global = useTranslations();

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const formik = useFormik({
        initialValues: {
            email: '',
        },
          
        validate: ValidateForm,
        
        onSubmit: async (values) => {
            setFormSubmitting(true);

            const emailTemplateConfig = emailTemplateConfigInfo(values.email);
            sendEmail(emailTemplateConfig.subject, emailTemplateConfig.html).then((response: any) => {
                setFormSubmitting(false);
                setFormSubmitted(true);
            }).catch((error: any) => {
                console.error('error', error);
                setFormSubmitting(false);
            });
        }
    });

    type Event = {
        target: {
            name: string;
            value: string;
        };
    };

    const handleFormChange = (e: Event) => {
        formik.handleChange(e);
    }

    const handleFormBlur = (e: Event) => {
        formik.handleBlur(e);
    }

    const errors = formik.errors;
    const touched = formik.touched;
    const values = formik.values;

    return (
        <>
            <div id='lease-packages-info-modal' className={`fixed top-0 left-0 w-full h-screen-mobile z-50 flex justify-center items-center ${open ? 'block' : 'hidden'}`} onClick={handleClose}>
                <div className={`w-screen-4/5 h-fit max-h-screen-4/5 overflow-y-auto package-modal-scrollbar bg-primary-gray-950 py-12 px-12 rounded-lg relative flex flex-col`} onClick={(e) => e.stopPropagation()}>
                    <Image
                        src='Icons/Close_hjrcnb'
                        alt='Close Lease Packages Modal'
                        width={16}
                        height={16}
                        className='absolute top-6 right-6 cursor-pointer'
                        onClick={handleClose}
                    />

                    <Typography variant='h3' className='text-white capitalize font-bold'>
                        {t_global("shared.lease_packages_info_modal.title")}
                    </Typography>

                    {formSubmitted ? (
                        <div className='mt-4'>
                            <Typography variant='body1' className='text-white'>
                                {t_global("shared.lease_packages_info_modal.thank_you_message")}
                            </Typography>
                        </div>
                    ) : (
                        <>
                            <div className='mt-4'>
                                <Typography variant='body1' className='text-white'>
                                    {t_global("shared.lease_packages_info_modal.text")}
                                </Typography>
                            </div>

                            <form className="flex flex-col md:flex-row items-center gap-x-8 gap-y-4" onSubmit={formik.handleSubmit}>
                                <div className="flex flex-col gap-2 md:w-1/2">
                                    <div className="field flex-grow">
                                        <div className="transition rounded-xl bg-white border border-primary-gray-300">
                                            <Input className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="email" name="email" placeholder={t_global('email')} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.email} required/>
                                        </div>
                                        <div className="absolute">
                                            <ErrorMessage>{errors.email && touched.email && t_global(errors.email)}</ErrorMessage>
                                        </div>
                                    </div>
                                </div>

                                <ButtonNew text={formSubmitting ? t_global('requesting_download') : t_global('request_download')} type="button" disabled={formSubmitting} theme={'dark'} className="ml-0" />
                            </form>
                        </>
                    )}
                </div>
            </div>


            <section id={id} className="py-16 bg-white scroll-mt-24">
                {disableComparisonTable ? <></> : (
                    <Container className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <MoveFadeInAnimation direction='right' delay={200}>
                                <Typography variant="h3" className='text-primary-gray-950 capitalize font-bold'>
                                    {t("packages.texts.0.title")}
                                </Typography>
                            </MoveFadeInAnimation>

                            <p className='text-primary-gray-950 max-w-2xl' dangerouslySetInnerHTML={{
                                __html: t("packages.texts.0.text")
                            }} />
                        </div>
                        <div className='md:col-span-2'>
                            <Table className='md:-mt-16'>
                                <Table.Body>
                                    <Table.Row className='bg-primary-gray-950'>
                                        <Table.Cell>
                                            &nbsp;
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-yellow-400 font-bold'>
                                                Onze Financial Lease
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-yellow-400 font-bold'>
                                                Traditioneel (eenmalig)
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                Direct eigendom
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌ leverancier afhankelijk
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                Geen opstartkosten
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                Vast maandbedrag
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                Incl. Domein & hosting
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                Incl. Support & onderhoud
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                Incl. Kleine aanpassingen
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ISO-gecertificeerde leverancier
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ✅
                                            </Typography>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Typography variant="body1" className='text-primary-gray-950'>
                                                ❌ meestal niet
                                            </Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table>
                        </div>
                    </Container>
                )}

                {disableInfoText ? <></> : (
                    <Container className="mt-12 mb-12 grid grid-cols-1 md:grid-cols-1 gap-6">
                        <div>
                            <p className='text-primary-gray-950 max-w-2xl' dangerouslySetInnerHTML={{
                                __html: t("packages.texts.1.text")
                            }} />
                        </div>

                        <div className='mt-4 flex gap-x-4'>
                            <ButtonOne variant="green" type="link" rel="nofollow" link={'https://wa.me/31610039472'} target='_blank' text={'Bericht ons via WhatsApp'} hoverEffect={true} marginClassName="m-0" className='w-fit hidden md:block' />
                            <ButtonOne variant="green" type="link" rel="nofollow" link={'https://wa.me/31610039472'} target='_blank' text={'WhatsApp'} hoverEffect={true} marginClassName="m-0" className='w-fit block md:hidden' />
                            <ButtonOne variant="border-green" type="button" onclick={() => handleOpen()} text={'Uitgebreide pakket beschrijving aanvragen'} hoverEffect={true} marginClassName="m-0" className='w-fit hidden md:block' />
                            <ButtonOne variant="border-green" type="button" onclick={() => handleOpen()} text={'Meer info'} hoverEffect={true} marginClassName="m-0" className='w-fit block md:hidden' />
                        </div>
                    </Container>
                )}

                {disablePackagesSection ? <></> : (
                    <Container id='price' className={`grid grid-cols-1 gap-6 scroll-mt-24 ${hideConfiguratorPackage ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
                        {packages.map((pkg, index) => (
                            <div key={pkg.id}>
                                <Typography variant="h4" className='text-primary-gray-950 capitalize font-bold'>
                                    {t(`packages.items.${index}.title`)}
                                </Typography>

                                <Typography variant="body1" className='text-primary-gray-950' dangerouslySetInnerHTML={{
                                    __html: convertMarkdownBolds(t(`packages.items.${index}.description`))
                                }}></Typography>

                                <Table className='mt-8'>
                                    <Table.Header titleAlignment='right' className='flex flex-wrap justify-end items-center'>
                                        {index === 0 && (
                                            <Typography variant="h5" className='text-white/65 lowercase'>
                                                {t(`packages.items.${index}.price_prefix`)}&nbsp;
                                            </Typography>
                                        )}

                                        <Typography variant="h5" className='text-primary-yellow-t lowercase'>
                                            {t(`packages.items.${index}.price`)}
                                        </Typography>
                                    </Table.Header>
                                    <Table.Body>
                                        <Table.Row>
                                            <Table.Cell>
                                                <Typography variant="body1" className='text-primary-gray-950'>
                                                    {t(`packages.items.${index}.name`)}
                                                </Typography>
                                            </Table.Cell>
                                        </Table.Row>

                                        {Array.from({ length: pkg.contentCount }).map((_, contentIndex) => (
                                            <Table.Row key={contentIndex}>
                                                <Table.Cell>
                                                    <Typography variant="body1" className='text-primary-gray-950'>
                                                        <span dangerouslySetInnerHTML={{
                                                            __html: t(`packages.items.${index}.contents.${contentIndex}.name`)
                                                        }} />
                                                    </Typography>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Typography variant="body1" className='text-primary-gray-950'>
                                                        {t(`packages.items.${index}.contents.${contentIndex}.value`)}
                                                    </Typography>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}

                                        <Table.Row>
                                            <Table.Cell>
                                                <Typography variant="body1" className='text-transparent opacity-0'>
                                                    a
                                                </Typography>
                                            </Table.Cell>
                                        </Table.Row>
                                    </Table.Body>
                                </Table>

                                <MoveFadeInAnimation direction='right' delay={index * 100}>
                                    {buttonType === 'new' ? (
                                        <ButtonNew
                                            id={`${pkg.id}-package-button`}
                                            text={t(`packages.items.${index}.button`)}
                                            type='button'
                                            onclick={() => dispatchLeasePackagesModalOpen(pkg.id)}
                                            noMargin={true}
                                            className='-translate-y-1/5 ml-4 mt-1'
                                        />
                                    ) : buttonType === 'old' ? (
                                        <ButtonOne
                                            paddingX='extra-small'
                                            customIcon={
                                                <div className='bg-primary-yellow-t rounded-lg p-2'>
                                                    <Cursor color='fill-primary-yellow-400' />
                                                </div>
                                            }
                                            iconPos='left'
                                            variant='green'
                                            text={t_global(`pages.lease_a_website.pricing.order_subscription`)}
                                            link={route('/request-website-lease', params.locale, `?package=${index}`)}
                                            className='w-full -mt-10 relative z-10'
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </MoveFadeInAnimation>
                            </div>
                        ))}

                        {!hideConfiguratorPackage && <div>
                            <Table className='mt-8'>
                                <Table.Header titleAlignment='right' className='flex flex-wrap items-center'>
                                    <Typography variant="h5" className='text-primary-yellow-400 capitalize'>
                                        {t(`packages.items.3.title`)}
                                    </Typography>
                                </Table.Header>
                                <Table.Body className='px-4 py-2'>
                                    <Typography variant='h5' className='text-primary-gray-950 capitalize font-semibold'>
                                        {t(`packages.items.3.titles.0`)}
                                    </Typography>
                                    <Typography variant='body1' className='text-primary-gray-950'>
                                        {t(`packages.items.3.texts.0`)}
                                    </Typography>

                                    <Typography variant='h6' className='text-primary-gray-950 capitalize font-semibold mt-4'>
                                        {t(`packages.items.3.titles.1`)}
                                    </Typography>
                                    <Typography variant='body1' className='text-primary-gray-950'>
                                        {t(`packages.items.3.texts.1`)}
                                    </Typography>

                                    <Typography variant='h6' className='text-primary-gray-950 capitalize font-semibold mt-4'>
                                        {t(`packages.items.3.titles.2`)}
                                    </Typography>
                                    <Typography variant='body1' className='text-primary-gray-950'>
                                        {t(`packages.items.3.texts.2`)}
                                    </Typography>

                                    <Typography variant='body1' className='text-primary-gray-950 mt-6'>
                                        {t(`packages.items.3.interested`)}
                                    </Typography>

                                    <br />
                                </Table.Body>
                            </Table>

                            <MoveFadeInAnimation direction='right' delay={3 * 100}>
                                {buttonType === 'new' ? (
                                    <ButtonNew
                                        id='configurator-package-button'
                                        text={t(`packages.items.3.button`)}
                                        type='button'
                                        onclick={() => dispatchLeasePackagesModalOpen('configurator')}
                                        noMargin={true}
                                        className='-translate-y-1/5 ml-4 mt-1'
                                    />
                                ) : buttonType === 'old' ? (
                                    <ButtonOne
                                        icon='Icons/arrow_drop_down_circle_msko7m'
                                        iconPos='right'
                                        variant="green"
                                        type="button"
                                        onclick={() => dispatchLeasePackagesModalOpen('configurator')}
                                        text={t_global(`pages.lease_a_website.pricing.order_subscription`)}
                                        hoverEffect={true}
                                        marginClassName="m-0"
                                        className='mt-1'
                                    />
                                ) : (
                                    <></>
                                )}
                            </MoveFadeInAnimation>
                        </div>}
                    </Container>
                )}

                {disableWarningText ? <></> : (
                    <Container>
                        <Typography variant="body1" className='text-primary-gray-950'>
                            {t("packages.warning")}
                        </Typography>
                    </Container>
                )}
            </section>
        </>
    );
}

export default PricingSectionFinancialLease;